import { haversineDistanceM, offsetLatLng } from './channelMapUtils';
import { deviceMapCoordinates } from './publicWifiRrmPlanUi';
import { MAP_UI } from '../theme/mapViewUi';

/** Minimum polygon buffer (m) around connected walk points. */
export const DEFAULT_LAMP_COVERAGE_BUFFER_M = 12;
const RSSI_COLOR_MIN = -95;
const RSSI_COLOR_MAX = -45;

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}

function hexToRgb(hex) {
  const normalized = String(hex || '').replace('#', '');
  if (normalized.length !== 6) return { r: 158, g: 158, b: 158 };
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function rgbToHex(r, g, b) {
  const toHex = (n) => Math.round(n).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function interpolateHex(fromHex, toHex, t) {
  const a = hexToRgb(fromHex);
  const b = hexToRgb(toHex);
  const mix = clamp01(t);
  return rgbToHex(
    a.r + (b.r - a.r) * mix,
    a.g + (b.g - a.g) * mix,
    a.b + (b.b - a.b) * mix,
  );
}

/** Map RSSI (dBm) to polygon stroke/fill colors — green (strong) → red (weak). */
export function rssiToCoverageColors(rssi) {
  if (rssi == null || !Number.isFinite(rssi)) {
    return {
      stroke: '#9e9e9e',
      fillOpacity: 0.18,
      rssiLabel: '—',
    };
  }
  const t = clamp01((rssi - RSSI_COLOR_MIN) / (RSSI_COLOR_MAX - RSSI_COLOR_MIN));
  const stroke = interpolateHex(MAP_UI.throughputPoor, MAP_UI.throughputExcellent, t);
  return {
    stroke,
    fillOpacity: 0.12 + t * 0.28,
    rssiLabel: `${Math.round(rssi)} dBm`,
  };
}

export function normalizeMac(mac) {
  return String(mac || '').toLowerCase().replace(/[^a-f0-9]/g, '');
}

export function formatMac(mac) {
  const normalized = normalizeMac(mac);
  if (normalized.length !== 12) return String(mac || '');
  return normalized.match(/.{2}/g).join(':');
}

function isConnectedLampSample(sample) {
  return Boolean(sample?.isConnectedBssid || sample?.is_connected_bssid);
}

/** Own-network walk samples: connected AP or any target-SSID / inventory BSSID reading. */
export function isOwnNetworkLampSample(sample, macIndex = null) {
  if (isConnectedLampSample(sample)) return true;
  if (sample?.isTargetSsid || sample?.is_target_ssid) return true;
  if (macIndex) {
    const bssid = normalizeMac(sample?.bssid);
    if (bssid.length === 12 && macIndex.has(bssid)) return true;
  }
  return false;
}

function sampleCoords(sample) {
  const lat = Number(sample?.latitude);
  const lng = Number(sample?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  const accuracyMeters = Number(sample?.accuracyMeters ?? sample?.accuracy_meters);
  return {
    lat,
    lng,
    accuracyMeters: Number.isFinite(accuracyMeters) ? accuracyMeters : null,
  };
}

function sampleRssi(sample) {
  const rssi = Number(sample?.rssiDbm ?? sample?.rssi_dbm);
  return Number.isFinite(rssi) ? rssi : null;
}

/** Map normalized radio/AP MAC → device metadata. */
export function buildDeviceMacIndex(devices, publicWifiMap = {}) {
  const index = new Map();

  const register = (mac, device, extra = {}) => {
    const normalized = normalizeMac(mac);
    if (!normalized || normalized.length !== 12) return;
    const serial = String(device?.serial_number || '').toLowerCase();
    if (!serial) return;
    const coords = deviceMapCoordinates(device);
    index.set(normalized, {
      serial,
      deviceName: device.name || device.serial_number,
      radioName: extra.radioName || '',
      interfaceName: extra.interfaceName || '',
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
    });
  };

  (devices || []).forEach((device) => {
    (device?.radios || []).forEach((radio) => {
      register(radio?.mac, device, { radioName: radio?.name || '' });
    });

    const pw = device?.public_wifi || {};
    const interfaces = pw.interfaces || pw.Interfaces || [];
    interfaces.forEach((iface) => {
      register(iface?.mac || iface?.MAC, device, { interfaceName: iface?.name || iface?.Name || '' });
    });

    const serial = String(device?.serial_number || '').toLowerCase();
    const entry = publicWifiMap[serial];
    (entry?.interfaces || []).forEach((iface) => {
      register(iface?.mac || iface?.MAC, device, { interfaceName: iface?.name || '' });
    });
  });

  return index;
}

function gpsPointKey(lat, lng) {
  return `${Number(lat).toFixed(6)},${Number(lng).toFixed(6)}`;
}

function uniqueWalkPoints(samples) {
  const byKey = new Map();
  (samples || []).forEach((sample) => {
    const coords = sampleCoords(sample);
    if (!coords) return;
    const key = gpsPointKey(coords.lat, coords.lng);
    const rssi = sampleRssi(sample);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        ...coords,
        rssiDbm: rssi,
        colors: rssiToCoverageColors(rssi),
      });
      return;
    }
    if (rssi != null && (existing.rssiDbm == null || rssi > existing.rssiDbm)) {
      byKey.set(key, {
        ...coords,
        rssiDbm: rssi,
        colors: rssiToCoverageColors(rssi),
      });
    }
  });
  return [...byKey.values()];
}

function cross(o, a, b) {
  return (a.lng - o.lng) * (b.lat - o.lat) - (a.lat - o.lat) * (b.lng - o.lng);
}

function convexHull(points) {
  if (points.length <= 1) return points;
  const sorted = [...points].sort((a, b) => a.lng - b.lng || a.lat - b.lat);
  const lower = [];
  sorted.forEach((p) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  });
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

function circlePolygon(lat, lng, radiusM, segments = 20) {
  const out = [];
  for (let i = 0; i < segments; i += 1) {
    const angle = (2 * Math.PI * i) / segments;
    out.push(offsetLatLng(lat, lng, radiusM, angle));
  }
  return out;
}

function centroid(points) {
  const lat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  const lng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
  return { lat, lng };
}

function bearingRad(from, to) {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return Math.atan2(y, x);
}

function expandPolygonOutward(polygon, expandM) {
  if (!polygon?.length || expandM <= 0) return polygon;
  const center = centroid(polygon);
  return polygon.map((p) => {
    const angle = bearingRad(center, p);
    return offsetLatLng(p.lat, p.lng, expandM, angle);
  });
}

export function buildCoveragePolygonFromPoints(points, bufferM = DEFAULT_LAMP_COVERAGE_BUFFER_M) {
  if (!points?.length) return [];
  const avgAccuracy = points.reduce((sum, p) => sum + (p.accuracyMeters || 0), 0) / points.length;
  const padding = Math.max(bufferM, avgAccuracy || 0, 8);

  if (points.length === 1) {
    return circlePolygon(points[0].lat, points[0].lng, padding);
  }

  if (points.length === 2) {
    const center = centroid(points);
    const maxDist = Math.max(
      ...points.map((p) => haversineDistanceM(center.lat, center.lng, p.lat, p.lng)),
    );
    return circlePolygon(center.lat, center.lng, maxDist + padding, 24);
  }

  const hull = convexHull(points);
  if (hull.length < 3) {
    const center = centroid(points);
    const maxDist = Math.max(
      ...points.map((p) => haversineDistanceM(center.lat, center.lng, p.lat, p.lng)),
    );
    return circlePolygon(center.lat, center.lng, maxDist + padding, 24);
  }

  return expandPolygonOutward(hull, padding * 0.5);
}

export function rssiToHeatmapWeight(rssi) {
  if (rssi == null || !Number.isFinite(rssi)) return 0.35;
  const t = clamp01((rssi - RSSI_COLOR_MIN) / (RSSI_COLOR_MAX - RSSI_COLOR_MIN));
  return 0.08 + t * 0.92;
}

/** Circle radius (m) for overlapping RSSI blobs on the map. */
export function rssiHeatmapCircleRadius(point) {
  const base = 12;
  const boost = point?.rssiDbm != null && point.rssiDbm > -72 ? 4 : 0;
  return base + boost;
}

/** Per-circle opacity so overlaps blend into a heatmap-like field. */
export function rssiHeatmapCircleOpacity(point) {
  const weight = point?.weight ?? rssiToHeatmapWeight(point?.rssiDbm);
  return Math.min(0.42, 0.16 + weight * 0.3);
}

/** Blue heatmap palette for a lamp selected from the list (distinct from default green/red RSSI). */
export function rssiToHighlightHeatmapColors(rssi) {
  if (rssi == null || !Number.isFinite(rssi)) {
    return {
      stroke: MAP_UI.rrmPlanMarker,
      fillOpacity: 0.28,
    };
  }
  const t = clamp01((rssi - RSSI_COLOR_MIN) / (RSSI_COLOR_MAX - RSSI_COLOR_MIN));
  const stroke = interpolateHex('#e3f2fd', '#1565c0', t);
  return {
    stroke,
    fillOpacity: 0.22 + t * 0.48,
  };
}

/** Stronger opacity for selected-lamp heatmap blobs. */
export function highlightHeatmapCircleOpacity(point) {
  const colors = rssiToHighlightHeatmapColors(point?.rssiDbm);
  const weight = point?.weight ?? rssiToHeatmapWeight(point?.rssiDbm);
  return Math.min(0.62, colors.fillOpacity + weight * 0.12);
}

/** Slightly larger blobs when a lamp is selected for heatmap focus. */
export function highlightHeatmapCircleRadius(point) {
  return Math.round(rssiHeatmapCircleRadius(point) * 1.15);
}

/** Default minimum RSSI (dBm) for acceptable connected-lamp coverage on the walk path. */
export const DEFAULT_COVERAGE_MIN_RSSI_DBM = -70;

export function formatSampleRssiLabel(rssiDbm) {
  if (rssiDbm == null || !Number.isFinite(Number(rssiDbm))) return 'RSSI unknown';
  return `${Math.round(rssiDbm)} dBm`;
}

export function computeMedianRssiDbm(rssiValues) {
  const values = (rssiValues || []).filter((v) => v != null && Number.isFinite(v));
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid];
  return Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

const BELOW_THRESHOLD_HEATMAP_COLOR = '#e53935';
const BELOW_THRESHOLD_DOT_STROKE = '#b71c1c';

/** True when RSSI is missing or strictly below the coverage threshold. */
export function isCoverageBelowThreshold(rssiDbm, minRssiDbm = DEFAULT_COVERAGE_MIN_RSSI_DBM) {
  const threshold = Number(minRssiDbm);
  if (!Number.isFinite(threshold)) {
    return rssiDbm == null || !Number.isFinite(Number(rssiDbm));
  }
  const rssi = Number(rssiDbm);
  if (!Number.isFinite(rssi)) return true;
  return rssi < threshold;
}

/** Walk samples at/above the min RSSI threshold — used for global coverage polygons. */
export function filterCoveragePointsAboveThreshold(points, minRssiDbm = DEFAULT_COVERAGE_MIN_RSSI_DBM) {
  return (points || []).filter((point) => !isCoverageBelowThreshold(point?.rssiDbm, minRssiDbm));
}

/** At each GPS fix, keep the strongest RSSI reading (across all APs/BSSIDs). */
export function aggregateBestRssiPointsByLocation(points) {
  const byKey = new Map();
  (points || []).forEach((point) => {
    if (!point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) return;
    const key = gpsPointKey(point.lat, point.lng);
    const rssi = point.rssiDbm;
    const existing = byKey.get(key);
    if (!existing || (rssi != null && (existing.rssiDbm == null || rssi > existing.rssiDbm))) {
      byKey.set(key, {
        ...point,
        rssiDbm: rssi,
        colors: rssiToCoverageColors(rssi),
        weight: rssiToHeatmapWeight(rssi),
      });
    }
  });
  return [...byKey.values()];
}

/** Global map overview: one dot per walk location showing the best RSSI heard there. */
export function buildOverviewCoveragePoints(lampCoverages, minRssiDbm = DEFAULT_COVERAGE_MIN_RSSI_DBM) {
  const allPoints = [];
  (lampCoverages || []).forEach((lamp) => {
    (lamp.heatmapPoints || []).forEach((point) => {
      allPoints.push({ ...point, bssid: lamp.bssid, lamp });
    });
  });
  const bestByLocation = aggregateBestRssiPointsByLocation(allPoints);
  return filterCoveragePointsAboveThreshold(bestByLocation, minRssiDbm);
}

/** Convex coverage footprint for one AP from qualifying walk samples only. */
export function buildLampCoveragePolygon(lamp, minRssiDbm = DEFAULT_COVERAGE_MIN_RSSI_DBM) {
  const qualifying = filterCoveragePointsAboveThreshold(lamp?.heatmapPoints, minRssiDbm);
  if (!qualifying.length) return [];
  return buildCoveragePolygonFromPoints(qualifying);
}

/** Map heatmap circle/dot styling for one connected sample point. */
export function resolveCoverageHeatmapPointStyle(point, {
  minRssiDbm = DEFAULT_COVERAGE_MIN_RSSI_DBM,
  isListSelected = false,
  isHighlighted = false,
  dimOthers = false,
  lampColors = null,
} = {}) {
  const radius = isListSelected ? highlightHeatmapCircleRadius(point) : rssiHeatmapCircleRadius(point);
  const belowThreshold = isCoverageBelowThreshold(point?.rssiDbm, minRssiDbm);

  if (belowThreshold) {
    const baseOpacity = isListSelected ? 0.58 : Math.max(rssiHeatmapCircleOpacity(point), 0.38);
    const fillOpacity = dimOthers ? baseOpacity * 0.15 : (isListSelected || isHighlighted ? baseOpacity : baseOpacity * 0.72);
    return {
      belowThreshold: true,
      fillColor: BELOW_THRESHOLD_HEATMAP_COLOR,
      fillOpacity,
      radius,
      dotFillOpacity: dimOthers ? 0.25 : (isListSelected ? 1 : 0.92),
      dotStrokeColor: BELOW_THRESHOLD_DOT_STROKE,
      dotStrokeWeight: isListSelected ? 2.5 : 2,
    };
  }

  const highlightColors = rssiToHighlightHeatmapColors(point?.rssiDbm);
  const fillColor = isListSelected
    ? highlightColors.stroke
    : (point?.colors?.stroke || lampColors?.stroke || MAP_UI.throughputExcellent);
  const baseOpacity = isListSelected
    ? highlightHeatmapCircleOpacity(point)
    : rssiHeatmapCircleOpacity(point);
  const fillOpacity = dimOthers ? baseOpacity * 0.12 : (isListSelected || isHighlighted ? baseOpacity : baseOpacity * 0.45);

  return {
    belowThreshold: false,
    fillColor,
    fillOpacity,
    radius,
    dotFillOpacity: dimOthers ? 0.2 : (isListSelected ? 1 : (isHighlighted ? 0.95 : 0.7)),
    dotStrokeColor: isListSelected ? MAP_UI.rrmPlanMarkerActive : '#ffffff',
    dotStrokeWeight: isListSelected ? 2.5 : (isHighlighted ? 2 : 1),
  };
}

/** Count connected walk samples below the coverage RSSI threshold. */
export function countCoverageBelowThreshold(points, minRssiDbm = DEFAULT_COVERAGE_MIN_RSSI_DBM) {
  return (points || []).filter((point) => isCoverageBelowThreshold(point?.rssiDbm, minRssiDbm)).length;
}

/** One heatmap datum per connected sample (includes repeat GPS fixes). */
export function buildLampHeatmapPoints(samples) {
  return (samples || [])
    .map((sample) => {
      const coords = sampleCoords(sample);
      if (!coords) return null;
      const rssi = sampleRssi(sample);
      return {
        lat: coords.lat,
        lng: coords.lng,
        weight: rssiToHeatmapWeight(rssi),
        rssiDbm: rssi,
        colors: rssiToCoverageColors(rssi),
      };
    })
    .filter(Boolean);
}

/** Group own-network (target SSID / connected / inventory) samples by AP BSSID. */
export function buildLampCoveragesFromSurvey(samples, devices, publicWifiMap = {}) {
  const macIndex = buildDeviceMacIndex(devices, publicWifiMap);
  const byBssid = new Map();

  (samples || []).forEach((sample) => {
    if (!isOwnNetworkLampSample(sample, macIndex)) return;
    const bssid = normalizeMac(sample?.bssid);
    if (!bssid) return;
    if (!byBssid.has(bssid)) byBssid.set(bssid, []);
    byBssid.get(bssid).push(sample);
  });

  const coverages = [];

  byBssid.forEach((groupSamples, bssid) => {
    const walkPoints = uniqueWalkPoints(groupSamples);
    if (!walkPoints.length) return;

    const device = macIndex.get(bssid) || null;
    const rssiValues = groupSamples.map(sampleRssi).filter((v) => v != null);
    const bestRssiDbm = rssiValues.length ? Math.max(...rssiValues) : null;
    const worstRssiDbm = rssiValues.length ? Math.min(...rssiValues) : null;
    const medianRssiDbm = computeMedianRssiDbm(rssiValues);
    const avgRssiDbm = rssiValues.length
      ? Math.round(rssiValues.reduce((a, b) => a + b, 0) / rssiValues.length)
      : null;
    const colors = rssiToCoverageColors(medianRssiDbm ?? bestRssiDbm);

    const ref = groupSamples[0] || {};

    coverages.push({
      id: bssid,
      bssid,
      bssidFormatted: formatMac(bssid),
      serial: device?.serial || null,
      deviceName: device?.deviceName || null,
      radioName: device?.radioName || device?.interfaceName || '',
      matchedDevice: Boolean(device),
      deviceGps: device?.lat != null && device?.lng != null
        ? { lat: device.lat, lng: device.lng }
        : null,
      ssid: ref.ssid || ref.SSID || '',
      channel: Number(ref.channel) > 0 ? Number(ref.channel) : null,
      frequencyMHz: Number(ref.frequencyMhz ?? ref.frequency_mhz) || null,
      sampleCount: groupSamples.length,
      walkPointCount: walkPoints.length,
      walkPoints,
      heatmapPoints: buildLampHeatmapPoints(groupSamples),
      bestRssiDbm,
      worstRssiDbm,
      medianRssiDbm,
      avgRssiDbm,
      colors,
    });
  });

  coverages.sort((a, b) => b.sampleCount - a.sampleCount || b.walkPointCount - a.walkPointCount);
  return coverages;
}

export function lampCoverageMapPoints(coverage) {
  if (coverage?.heatmapPoints?.length) return coverage.heatmapPoints;
  if (coverage?.walkPoints?.length) return coverage.walkPoints;
  return [];
}

export function lampCoverageCenter(coverage) {
  const points = lampCoverageMapPoints(coverage);
  if (!points.length) return null;
  return {
    lat: points.reduce((sum, p) => sum + p.lat, 0) / points.length,
    lng: points.reduce((sum, p) => sum + p.lng, 0) / points.length,
  };
}

export function lampCoverageBounds(coverage) {
  const points = lampCoverageMapPoints(coverage);
  if (!points.length) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  points.forEach((p) => {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  });
  if (!Number.isFinite(minLat)) return null;
  return { minLat, maxLat, minLng, maxLng };
}
